// client/src/components/AdminSidebar.js
// Left navigation sidebar for admin mode

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isSuperAdmin } from '../utils/roleUtils';
import { getAdminUrl } from '../utils/urlUtils';
import { LabelMedium } from './Typography';

const AdminSidebar = ({ activeSection, onSectionChange }) => {
  const { userRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const navigationItems = [
    {
      id: 'account',
      label: isSuperAdmin(userRole) ? 'Account' : 'Organization',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      action: () => navigate(getAdminUrl('account'))
    },
    // Only show Organizations for superadmins
    ...(isSuperAdmin(userRole) ? [{
      id: 'organizations',
      label: 'Organizations',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      action: () => navigate(getAdminUrl('organizations'))
    }] : []),
    // Only show Courses for superadmins
    ...(isSuperAdmin(userRole) ? [{
      id: 'courses',
      label: 'Courses',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
      action: () => navigate(getAdminUrl('courses'))
    }] : []),
    // Only show Lessons for superadmins
    ...(isSuperAdmin(userRole) ? [{
      id: 'lessons',
      label: 'Lessons',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      action: () => navigate(getAdminUrl('lessons'))
    }] : []),
    // Only show Prompts for superadmins
    ...(isSuperAdmin(userRole) ? [{
      id: 'prompts',
      label: 'Prompts',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      ),
      action: () => navigate(getAdminUrl('prompts'))
    }] : []),
    // Only show Reward Items for superadmins
    ...(isSuperAdmin(userRole) ? [{
      id: 'reward-items',
      label: 'Reward Items',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
        </svg>
      ),
      action: () => navigate(getAdminUrl('reward-items'))
    }] : []),
    // Only show Superadmins for superadmins
    ...(isSuperAdmin(userRole) ? [{
      id: 'superadmins',
      label: 'Super Admins',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      action: () => navigate(getAdminUrl('superadmins'))
    }] : []),
    {
      id: 'students',
      label: 'Students',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      action: () => navigate(getAdminUrl('students'))
    },
    {
      id: 'reporting',
      label: 'Reporting Dashboard',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      action: () => navigate(getAdminUrl('reporting'))
    },
    {
      id: 'marketing',
      label: 'Marketing',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
        </svg>
      ),
      action: () => window.open('https://docs.google.com/document/d/16J5NxzWTfu0PNnOfRZwgO7T0SVuyVTSk5wDbzVaXNd0/edit?usp=sharing', '_blank'),
      external: true
    }
  ];

  return (
    <div className="w-64 text-white h-screen flex flex-col" style={{ backgroundColor: '#202020' }}>
      {/* Storykin Brand */}
      <div className="p-6 border-b" style={{ borderColor: '#3a3a3a' }}>
        <div className="flex items-center space-x-3">
          <img 
            src="/images/storykin_logo.png" 
            alt="STORYKIN" 
            className="h-8 w-auto object-contain"
          />
        </div>
      </div>

      {/* Navigation Items */}
      <nav className="flex-1 py-6">
        <ul className="space-y-2 px-4">
          {navigationItems.map((item) => {
            // Check if current URL matches this navigation item
            const isActive = location.pathname === getAdminUrl(item.id) || 
                           (location.pathname.startsWith(`/admin/${item.id}`) && !location.pathname.includes('/edit'));
            
            return (
              <li key={item.id}>
                <button
                  onClick={item.action}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors duration-200 ${
                    isActive
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  <span className={isActive ? 'text-white' : 'text-gray-400'}>
                    {item.icon}
                  </span>
                  <LabelMedium color={isActive ? 'white' : 'gray-300'}>
                    {item.label}
                  </LabelMedium>
                  {item.external && (
                    <svg className="w-3 h-3 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Footer */}
      <div className="p-4 border-t" style={{ borderColor: '#3a3a3a' }}>
        <div className="text-xs text-gray-400 text-center">
          Admin Mode
        </div>
      </div>
    </div>
  );
};

export default AdminSidebar;
